// src/pages/Brands.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  addBrand,
  listBrands,
  deleteBrand,
  updateBrand,
} from "../services/brandService";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebaseConfig";
import { ToastContainer, toast } from "react-toastify";

/* Helpers */
const safe = (v) => (typeof v === "string" ? v.trim() : "");
const slugify = (s) =>
  safe(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

function guessExt(file) {
  const name = file?.name || "";
  const m = name.match(/\.(\w+)$/);
  if (m?.[1]) return m[1].toLowerCase();
  const type = file?.type || "";
  if (type.includes("png")) return "png";
  if (type.includes("webp")) return "webp";
  if (type.includes("jpeg") || type.includes("jpg")) return "jpg";
  return "jpg";
}

export default function Brands() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ name: "", slug: "", logo: "", logoPath: "" });
  const [file, setFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // ✅ preview propre (pas de URL.createObjectURL dans le JSX)
  const preview = useMemo(() => {
    if (!file) return "";
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  // ✅ load stable (plus de warning exhaustive-deps)
  const load = useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const res = await listBrands({
          pageSize: 20,
          cursor: reset ? null : cursor,
        });
        if (reset) setItems(res.items || []);
        else setItems((prev) => [...prev, ...(res.items || [])]);

        setCursor(res.nextCursor || null);
      } catch {
        toast.error("Erreur de chargement");
      } finally {
        setLoading(false);
      }
    },
    [cursor]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const uploadLogo = useCallback(async () => {
    if (!file) return { url: "", path: "" };

    if (!file.type?.startsWith("image/")) {
      throw new Error("Veuillez choisir une image");
    }
    if (file.size > 4 * 1024 * 1024) {
      throw new Error("Image trop lourde (max 4MB)");
    }

    const ext = guessExt(file);
    const computedSlug = slugify(form.slug || form.name || "brand");
    const path = `brands/${computedSlug}-${Date.now()}.${ext}`;

    const r = ref(storage, path);
    await uploadBytes(r, file, {
      contentType: file.type || "image/jpeg",
      cacheControl: "public,max-age=31536000",
    });

    const url = await getDownloadURL(r);
    return { url, path };
  }, [file, form.slug, form.name]);

  const resetForm = () => {
    setForm({ name: "", slug: "", logo: "", logoPath: "" });
    setFile(null);
    setEditing(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      const computedSlug = safe(form.slug) ? safe(form.slug) : slugify(form.name);

      let logo = form.logo || "";
      let logoPath = form.logoPath || "";

      if (file) {
        const up = await uploadLogo();
        logo = up.url;
        logoPath = up.path;
      }

      const payload = {
        name: safe(form.name),
        slug: computedSlug,
        logo,
        logoPath,
        updatedAt: Date.now(),
      };

      if (editing) {
        await updateBrand(editing.id, payload);
        toast.success("Marque mise à jour");
      } else {
        await addBrand({ ...payload, createdAt: Date.now() });
        toast.success("Marque ajoutée");
      }

      resetForm();
      load(true);
    } catch (err) {
      toast.error(err?.message || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || "",
      slug: b.slug || "",
      logo: b.logo || "",
      logoPath: b.logoPath || "",
    });
    setFile(null);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette marque ?")) return;
    try {
      await deleteBrand(id);
      toast.success("Supprimé");
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      toast.error(e?.message || "Erreur suppression");
    }
  };

  return (
    <div className="container py-4">
      <ToastContainer position="top-right" />

      <div className="row g-4">
        <div className="col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="m-0">
                  {editing ? "Modifier la marque" : "Ajouter une marque"}
                </h5>
                {editing ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={resetForm}
                    disabled={saving}
                  >
                    Annuler
                  </button>
                ) : null}
              </div>

              <form onSubmit={onSubmit}>
                <div className="mb-3">
                  <label className="form-label">Nom</label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    required
                    disabled={saving}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Slug (auto si vide)</label>
                  <input
                    className="form-control"
                    value={form.slug}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, slug: e.target.value }))
                    }
                    placeholder={slugify(form.name) || "ex: nike"}
                    disabled={saving}
                  />
                  <div className="form-text">
                    Recommandé: <code>{slugify(form.slug || form.name) || "brand"}</code>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    disabled={saving}
                  />

                  {(file || form.logo) && (
                    <div className="mt-2 d-flex align-items-center gap-2">
                      <img
                        src={file ? preview : form.logo}
                        alt="logo"
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          borderRadius: 10,
                          border: "1px solid rgba(0,0,0,.08)",
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setFile(null)}
                        disabled={saving}
                      >
                        Retirer
                      </button>
                    </div>
                  )}
                </div>

                <div className="d-grid">
                  <button className="btn btn-success" disabled={saving}>
                    {saving ? "Enregistrement..." : editing ? "Mettre à jour" : "Ajouter"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="m-0">Marques</h5>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => load(true)}
                  disabled={loading || saving}
                >
                  Rafraîchir
                </button>
              </div>

              <div className="table-responsive">
                <table className="table align-middle table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>Logo</th>
                      <th>Nom</th>
                      <th>Slug</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((b) => (
                      <tr key={b.id}>
                        <td>
                          {b.logo ? (
                            <img
                              src={b.logo}
                              alt="logo"
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                              loading="lazy"
                            />
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="fw-semibold">{b.name}</td>
                        <td className="text-muted small">{b.slug}</td>
                        <td className="text-end">
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => onEdit(b)}
                              disabled={saving}
                            >
                              Éditer
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => onDelete(b.id)}
                              disabled={saving}
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-grid">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => load(false)}
                  disabled={!cursor || loading || saving}
                >
                  {loading ? "Chargement..." : cursor ? "Charger plus" : "Fin de liste"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
